import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@rapider/environments';
import { TraceNode } from '../ai-chat.config';

@Injectable({
  providedIn: 'root',
})
export class AgentTraceService {
  constructor(private http: HttpClient) {}

  getTrace(messageId: string): Observable<TraceNode[]> {
    const url = `${environment.apiBaseUrl}/project-channel-messages/direct-messages/${messageId}/trace`;

    return this.http
      .get<TraceNode[]>(url)
      .pipe(map((nodes) => this.processTraceNodes(nodes)));
  }

  getDebugAnalysisUrl(messageId: string): Observable<{ url: string }> {
    const url = `${environment.apiBaseUrl}/project-channel-messages/direct-messages/${messageId}/debug-analysis`;
    return this.http.get<{ url: string }>(url);
  }

  private processTraceNodes(nodes: TraceNode[]): TraceNode[] {
    if (!nodes || !Array.isArray(nodes)) return [];

    const cumulativeState: Record<string, any> = {};

    return nodes.map((node, index) => {
      // 1. Determine Node Name
      let nodeName = node.name || 'Unknown Node';
      if (nodeName === 'Unknown Node') {
        if (node.writes && Object.keys(node.writes).length > 0) {
          nodeName = Object.keys(node.writes)[0];
        } else if (node.step === -1 && node.source === 'input') {
          nodeName = 'User Input';
        } else if (node.source) {
          nodeName = node.source;
        }
      }

      // 2. Calculate Duration
      let durationMs = 0;
      if (index > 0) {
        const currentMs = new Date(node.createdAt).getTime();
        const prevMs = new Date(nodes[index - 1].createdAt).getTime();
        durationMs = Math.max(0, currentMs - prevMs);
      }

      // 3. Extract Mutations from pre-calculated diffs in state to build iterative state
      const currentState = node.state || {};
      const isDiffFormat =
        currentState.added !== undefined ||
        currentState.deleted !== undefined ||
        currentState.updated !== undefined;

      const mergedState: Record<
        string,
        { value: any; type: 'added' | 'deleted' | 'updated' | 'unchanged' }
      > = {};

      // Initialize with previous cumulative state
      Object.keys(cumulativeState).forEach((key) => {
        mergedState[key] = { value: cumulativeState[key], type: 'unchanged' };
      });

      if (isDiffFormat) {
        if (currentState.deleted) {
          Object.keys(currentState.deleted).forEach((key) => {
            mergedState[key] = {
              value: currentState.deleted[key],
              type: 'deleted',
            };
            delete cumulativeState[key]; // Removed from NEXT step's base state
          });
        }
        if (currentState.updated) {
          Object.keys(currentState.updated).forEach((key) => {
            mergedState[key] = {
              value: currentState.updated[key],
              type: 'updated',
            };
            cumulativeState[key] = currentState.updated[key]; // Update for next step
          });
        }
        if (currentState.added) {
          Object.keys(currentState.added).forEach((key) => {
            mergedState[key] = {
              value: currentState.added[key],
              type: 'added',
            };
            cumulativeState[key] = currentState.added[key]; // Update for next step
          });
        }
      } else {
        // Fallback for older traces without pre-calculated diffs
        Object.keys(currentState).forEach((key) => {
          mergedState[key] = { value: currentState[key], type: 'added' };
          cumulativeState[key] = currentState[key];
        });
      }

      const stateIterative = Object.keys(mergedState)
        .map((key) => ({ key, ...mergedState[key] }))
        .sort((a, b) => a.key.localeCompare(b.key));

      return {
        ...node,
        nodeName,
        durationMs,
        stateIterative,
      };
    });
  }
}
